/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useRef, useState } from "react";
import { Outlet } from "react-router";

import { useToast } from "sj-ui-toolkit/src/hooks";

import {
  ActivityData,
  MentorRequestStatus,
  fetchDojoActivity,
  fetchMentorRequests,
} from "@/api/activities";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MentorFiltersState {
  selectedStatus: "all" | MentorRequestStatus;
  selectedAdmins: string[];
  selectedUsers: string[];
}

interface MentorRequestsContextValue {
  allActivities: ActivityData[];
  loading: boolean;
  loadingClosed: boolean;
  error: string | null;
  loadMentorRequests: () => Promise<void>;
  updateActivity: (activityId: number, updates: Partial<ActivityData>) => void;
  filters: MentorFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<MentorFiltersState>>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const deduplicateActivities = (activities: ActivityData[]): ActivityData[] =>
  Array.from(new Map(activities.map(a => [a.id, a])).values());

const fetchActivityDetails = async (
  mentorRequests: Awaited<ReturnType<typeof fetchMentorRequests>>,
  requestStatus: MentorRequestStatus
): Promise<ActivityData[]> => {
  const results = await Promise.all(
    mentorRequests.map(async req => {
      const activityData = await fetchDojoActivity(req.dojoActivityId, "mentor_request");
      if (!activityData) return null;
      const entry: ActivityData = {
        id: activityData.id,
        text: activityData.text,
        description:
          typeof activityData.description === "string" ? activityData.description : undefined,
        createdAt: req.createdAt,
        userName: req.userName,
        requestStatus,
      };
      return entry;
    })
  );
  return results.filter((a): a is ActivityData => a !== null);
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const MentorRequestsContext = createContext<MentorRequestsContextValue | null>(null);

const DEFAULT_FILTERS: MentorFiltersState = {
  selectedStatus: "open",
  selectedAdmins: [],
  selectedUsers: [],
};

// ---------------------------------------------------------------------------
// Provider (used as a layout route wrapper in App.tsx)
// ---------------------------------------------------------------------------

export const MentorRequestsProvider = () => {
  const { showToast } = useToast();

  const [allActivities, setAllActivities] = useState<ActivityData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingClosed, setLoadingClosed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<MentorFiltersState>(DEFAULT_FILTERS);

  const hasLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);

  const loadMentorRequests = useCallback(async () => {
    if (hasLoadedRef.current || isLoadingRef.current) return;
    isLoadingRef.current = true;

    try {
      setLoading(true);
      setError(null);

      const openRequests = await fetchMentorRequests({
        assigned_to: "anyone",
        order: "desc",
        status: "open",
      });
      const openActivities = await fetchActivityDetails(openRequests, "open");
      setAllActivities(deduplicateActivities(openActivities));
      setLoading(false);

      setLoadingClosed(true);
      const closedRequests = await fetchMentorRequests({
        assigned_to: "anyone",
        order: "desc",
        status: "closed",
      });
      const closedActivities = await fetchActivityDetails(closedRequests, "closed");
      setAllActivities(prev => deduplicateActivities([...prev, ...closedActivities]));
      setLoadingClosed(false);
      hasLoadedRef.current = true;
    } catch (err) {
      console.error("Failed to load mentor requests:", err);
      setError("Failed to load mentor requests");
      showToast({ severity: "error", summary: "Error", detail: "Failed to load mentor requests" });
      setLoading(false);
      setLoadingClosed(false);
    } finally {
      isLoadingRef.current = false;
    }
  }, [showToast]);

  const updateActivity = useCallback((activityId: number, updates: Partial<ActivityData>) => {
    setAllActivities(prev =>
      prev.map(activity => (activity.id === activityId ? { ...activity, ...updates } : activity))
    );
  }, []);

  return (
    <MentorRequestsContext.Provider
      value={{
        allActivities,
        loading,
        loadingClosed,
        error,
        loadMentorRequests,
        updateActivity,
        filters,
        setFilters,
      }}
    >
      <Outlet />
    </MentorRequestsContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const FALLBACK: Pick<MentorRequestsContextValue, "updateActivity"> = {
  updateActivity: () => {},
};

export const useMentorRequests = (): MentorRequestsContextValue => {
  const ctx = useContext(MentorRequestsContext);
  if (!ctx) {
    // Graceful fallback when used outside the provider (e.g. evaluate detail page)
    return {
      ...FALLBACK,
      allActivities: [],
      loading: false,
      loadingClosed: false,
      error: null,
      loadMentorRequests: async () => {},
      filters: {
        selectedStatus: "open",
        selectedAdmins: [],
        selectedUsers: [],
      },
      setFilters: () => {},
    };
  }
  return ctx;
};
